import { computed, readonly, shallowRef } from "vue";

import { jobs as jobsApi } from "~/api";

/**
 * Composable for fetching a job's paginated runs.
 *
 * Uses keyset pagination with a frontend cursor stack to support
 * backward navigation (Previous button).
 *
 * @param {string} jobId
 * @returns {{
 *   runs: import('vue').DeepReadonly<import('vue').ShallowRef<Array|null>>,
 *   loading: import('vue').DeepReadonly<import('vue').ShallowRef<boolean>>,
 *   error: import('vue').DeepReadonly<import('vue').ShallowRef<string|null>>,
 *   hasPreviousPage: import('vue').ComputedRef<boolean>,
 *   hasNextPage: import('vue').ComputedRef<boolean>,
 *   endCursor: import('vue').ComputedRef<string|null>,
 *   fetchRuns: Function,
 *   goNext: Function,
 *   goPrev: Function,
 *   clearError: Function
 * }}
 */
function useJobRuns(jobId) {
  const state = createJobRunsState();

  async function fetchRuns(cursor) {
    state.loading.value = true;
    state.error.value = null;

    try {
      const result = await jobsApi.runs({ params: buildFetchParams(jobId, cursor) });
      updateRunsState(state, result, cursor);
    } catch (err) {
      state.error.value = err.message || "Oops, something went wrong!";
    } finally {
      state.loading.value = false;
    }
  }

  async function goNext() {
    const cursor = state.pagination.value.endCursor;
    if (!cursor) return;

    state.cursorStack.value = [...state.cursorStack.value, state.currentCursor.value];
    await fetchRuns(cursor);
  }

  async function goPrev() {
    if (state.cursorStack.value.length === 0) return;

    const stack = [...state.cursorStack.value];
    const previousCursor = stack.pop();
    state.cursorStack.value = stack;
    await fetchRuns(previousCursor);
  }

  function clearError() {
    state.error.value = null;
  }

  return readonlyJobRunsState(state, { clearError, fetchRuns, goNext, goPrev });
}

function createJobRunsState() {
  return {
    currentCursor: shallowRef(null),
    cursorStack: shallowRef([]),
    error: shallowRef(null),
    loading: shallowRef(false),
    pagination: shallowRef({ endCursor: null, hasNextPage: false }),
    runs: shallowRef(null)
  };
}

function buildFetchParams(jobId, cursor) {
  return cursor ? { id: jobId, query: { page: cursor } } : { id: jobId };
}

function updateRunsState(state, result, cursor) {
  state.runs.value = result.runs;
  state.currentCursor.value = cursor ?? null;
  state.pagination.value = {
    endCursor: result.pagination?.endCursor ?? null,
    hasNextPage: result.pagination?.hasNextPage ?? false
  };
}

function readonlyJobRunsState(state, actions) {
  return {
    ...actions,
    endCursor: computed(() => state.pagination.value.endCursor),
    error: readonly(state.error),
    hasNextPage: computed(() => state.pagination.value.hasNextPage),
    hasPreviousPage: computed(() => state.cursorStack.value.length > 0),
    loading: readonly(state.loading),
    runs: readonly(state.runs)
  };
}

export { useJobRuns };
